import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import FlexibleTitleInput from '@/components/Inputs/FlexibleTitle';
import type { SelectOptionT } from '@/components/Select/types';
import { AttrText } from '@/components/Typography/NamedTypo';
import PropertyValue from '@/components/Typography/PropertyValue';
import Typo from '@/components/Typography/Typo';
import AgentTagSelector from '@/features/Agent/components/AgnetTagSelector';
import useAgentStore from '@/features/Agent/stores';
import AttrSelectDropdown from '@/features/Commons/Dropdown/AttrSelectDropdown';
import useColor from '@/hooks/domain/useColor';
import useUserRole from '@/hooks/domain/useUserRole';
import { useInputs } from '@/hooks/pure/useInputs';
import type { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { useToast } from '@/stores/pure/ToastStore';
import useTagStore from '@/stores/TagStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { readableFormat } from '@/utils/time';

type Props = {
  agent?: AgentT;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

const AgentDetailTemplate = (props: Props) => {
  const addToast = useToast();
  const color = useColor();
  const { isEditor } = useUserRole();
  const tagMap = useTagStore((state) => state.tagMap);

  const { workspaceFieldOptions, updateWorkspaceFieldOptions } =
    useWorkspaceStore((state) => {
      return {
        workspaceFieldOptions: state.workspaceFieldOptions,
        updateWorkspaceFieldOptions: state.updateWorkspaceFieldOptions,
      };
    });

  const roleOptions: SelectOptionT[] = useMemo(() => {
    return workspaceFieldOptions.role_values.map((value) => ({
      value,
      searchName: value,
    }));
  }, [workspaceFieldOptions.role_values]);

  const teamOptions: SelectOptionT[] = useMemo(() => {
    return workspaceFieldOptions.team_values.map((value) => ({
      value,
      searchName: value,
    }));
  }, [workspaceFieldOptions.team_values]);

  const { fetchAgentList, changeAgentAttr, updateAgentTags } = useAgentStore();

  const [inputs, onChange] = useInputs({
    name: props.agent?.name || '',
  });

  useEffect(() => {
    if (props.agent?.name)
      onChange({ target: { name: 'name', value: props.agent?.name } });
  }, [props.agent?.name, onChange]);

  const onBlurHandler = () => {
    if (!props.agent) return;
    if (inputs.name !== props.agent.name) {
      if (inputs.name.trim() === '') {
        return addToast('Enter the name of the agent', 'fail');
      }
      changeAgentAttr(props.agent.alias, 'name', inputs.name);
    }
  };

  useEffect(() => {
    if (!props.agent) {
      fetchAgentList();
    }
  }, [fetchAgentList, props.agent]);

  if (!props.agent)
    return (
      <CenterFlex style={{ height: 300 }}>
        <Spinner />
      </CenterFlex>
    );

  return (
    <Style>
      <VFlex g="16px">
        <FlexibleTitleInput
          name="name"
          value={inputs.name}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          placeholder="Type agent name..."
          disabled={!isEditor}
          onBlur={onBlurHandler}
        />
        <VFlex>
          <Flex className="property-row">
            <AttrText className="property-label">Email address</AttrText>
            <PropertyValue value={props.agent?.email} />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Role</AttrText>
            <AttrSelectDropdown
              name={`agent-detail-${props.agent.alias}-role-dropdown`}
              view="detail"
              selected={props.agent.role || null}
              options={roleOptions}
              onSelect={(key) =>
                changeAgentAttr(props.agent!.alias, 'role', key)
              }
              onClear={() =>
                changeAgentAttr(props.agent!.alias, 'role', undefined)
              }
              onCreate={async (key) => {
                await updateWorkspaceFieldOptions(1, 'role', key);
                changeAgentAttr(props.agent!.alias, 'role', key);
              }}
              onDelete={(key) =>
                updateWorkspaceFieldOptions(4, 'role', key.value as string)
              }
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Team</AttrText>
            <AttrSelectDropdown
              name={`agent-detail-${props.agent.alias}-team-dropdown`}
              view="detail"
              selected={props.agent.team || null}
              options={teamOptions}
              onSelect={(key) =>
                changeAgentAttr(props.agent!.alias, 'team', key)
              }
              onClear={() =>
                changeAgentAttr(props.agent!.alias, 'team', undefined)
              }
              onCreate={async (key) => {
                await updateWorkspaceFieldOptions(1, 'team', key);
                changeAgentAttr(props.agent!.alias, 'team', key);
              }}
              onDelete={(key) =>
                updateWorkspaceFieldOptions(4, 'team', key.value as string)
              }
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Last activity</AttrText>
            {props.agent.last_activity_date ? (
              <PropertyValue
                value={readableFormat(props.agent.last_activity_date)}
              />
            ) : (
              <Typo
                type="label2"
                weight="r"
                color={color.gray500}
                style={{ paddingLeft: 8, fontSize: 12 }}
              >
                Last activity
              </Typo>
            )}
          </Flex>
          <Space h={6} />
          <Flex a="flex-start" g={10}>
            <AttrText className="property-label">Tag</AttrText>
            <AgentTagSelector
              name={`agent-detail-${props.agent.alias}-tags-standalone-dropdown`}
              selectedTags={props.agent.tags
                ?.map((l) => tagMap[l]?.alias)
                .filter((l) => l)}
              onConfirm={(tags) => updateAgentTags(props.agent!.alias, tags)}
            />
          </Flex>
        </VFlex>
      </VFlex>
    </Style>
  );
};

const Style = styled.div`
  padding: 16px 32px 0px 32px;
  .property-label {
    width: 90px;
    height: 24px;
    line-height: 24px;
  }

  .property-row {
    gap: 10px;
    align-items: center;
    height: 32px;
  }

  .email-input {
    font-size: 12px;
    height: 32px;

    &::placeholder {
      font-size: 14px;
    }
  }
`;

export default AgentDetailTemplate;
